import React, { useCallback, useEffect, useState } from 'react';
import { X, Camera, ChevronLeft, ChevronRight, Loader2, Gauge, Play, Square, Image } from 'lucide-react';
import { AllertMediaService } from '../services/apiService';
import { AllertMedia, SpringPage } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface Props {
    onClose: () => void;
}

// YYYY-MM-DD in ora locale, non UTC.
const toIsoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDateTime = (iso?: string): string => {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return iso;
    return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
};

// Lo scarto è quello che il titolare guarda per capire se la foto è
// plausibile: 3 minuti è distrazione, 6 ore è un'altra giornata.
const formatDiff = (sec?: number): string => {
    if (sec == null) return '—';
    const abs = Math.abs(sec);
    if (abs < 60) return `${abs}s`;
    if (abs < 3600) return `${Math.round(abs / 60)} min`;
    const h = Math.floor(abs / 3600);
    const m = Math.round((abs % 3600) / 60);
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const origineLabel = (r: AllertMedia): { text: string; icon: React.ReactElement } => {
    if (r.tipoMedia === 'TURNO_FOTO_START') return { text: 'Turno · start', icon: <Play size={11} /> };
    if (r.tipoMedia === 'TURNO_FOTO_STOP') return { text: 'Turno · stop', icon: <Square size={11} /> };
    if (r.categoria === 'KM') return { text: 'Foto km', icon: <Gauge size={11} /> };
    return { text: 'Media incarico', icon: <Image size={11} /> };
};

const riferimentoLabel = (r: AllertMedia): string => {
    if (r.turnoId) return r.servizioNome || `Turno #${r.turnoId}`;
    if (r.incaricoId) return `Incarico #${r.incaricoId}`;
    return '—';
};

// Registro in sola lettura delle foto caricate dalla coda offline dell'app con
// EXIF assente o fuori tolleranza (default ultimi 7 giorni). Nessuna azione per
// riga: la foto è già accettata, il registro serve solo a renderla visibile.
export const AllertMediaModal: React.FC<Props> = ({ onClose }) => {
    const { currentSedeId } = useAuth();
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [from, setFrom] = useState(toIsoDate(sevenDaysAgo));
    const [to, setTo] = useState(toIsoDate(today));
    const [page, setPage] = useState(0);
    const [size] = useState(20);
    const [data, setData] = useState<SpringPage<AllertMedia> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const sedeIdFilter = typeof currentSedeId === 'number' ? currentSedeId : undefined;
            const res = await AllertMediaService.search(from, to, page, size, sedeIdFilter);
            setData(res.data || null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Errore nel caricamento');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [from, to, page, size, currentSedeId]);

    useEffect(() => { load(); }, [load]);

    const onChangeFrom = (v: string) => { setFrom(v); setPage(0); };
    const onChangeTo = (v: string) => { setTo(v); setPage(0); };

    const totalElements = data?.totalElements ?? 0;
    const totalPages = data?.totalPages ?? 0;
    const rows = data?.content ?? [];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl lg:max-w-5xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-5 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <Camera size={20} className="text-amber-600" />
                        <div>
                            <h3 className="text-lg font-bold text-slate-800">Avvisi foto offline</h3>
                            <p className="text-xs text-slate-500">
                                Caricate senza rete, con data di scatto assente o incoerente. Solo informativo: le foto sono già state accettate.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Filtri */}
                <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-end flex-wrap gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Da</label>
                        <input
                            type="date"
                            value={from}
                            onChange={e => onChangeFrom(e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">A</label>
                        <input
                            type="date"
                            value={to}
                            onChange={e => onChangeTo(e.target.value)}
                            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
                        />
                    </div>
                    <div className="ml-auto text-xs text-slate-500">
                        {loading ? (
                            <span className="inline-flex items-center gap-1.5">
                                <Loader2 size={12} className="animate-spin" /> Caricamento…
                            </span>
                        ) : (
                            <span><span className="font-bold text-slate-700">{totalElements}</span> avvisi nel periodo</span>
                        )}
                    </div>
                </div>

                {/* Tabella */}
                <div className="max-h-[60vh] overflow-y-auto">
                    {error && (
                        <div className="p-5 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">{error}</div>
                    )}
                    {!error && !loading && rows.length === 0 && (
                        <div className="p-10 text-center text-sm text-slate-400">
                            Nessun avviso nel periodo selezionato.
                        </div>
                    )}
                    {rows.length > 0 && (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Quando</th>
                                    <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Origine</th>
                                    <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Riferimento</th>
                                    <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Collaboratore</th>
                                    <th className="px-3 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Anomalia</th>
                                    <th className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Scatto EXIF</th>
                                    <th className="px-3 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase border-b border-slate-200">Scarto</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map(r => {
                                    const isMissing = r.tipoAnomalia === 'EXIF_MISSING';
                                    const origine = origineLabel(r);
                                    return (
                                        <tr key={r.id} className="hover:bg-slate-50">
                                            <td className="px-3 py-2.5 font-mono text-[12px] text-slate-700">{formatDateTime(r.createdAt)}</td>
                                            <td className="px-3 py-2.5">
                                                <span className="inline-flex items-center gap-1 text-slate-700">
                                                    {origine.icon} {origine.text}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-slate-700">{riferimentoLabel(r)}</td>
                                            <td className="px-3 py-2.5 text-slate-700">{r.collaboratoreNome || '—'}</td>
                                            <td className="px-3 py-2.5 text-center">
                                                <span className={
                                                    'inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ' +
                                                    (isMissing ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-800')
                                                }>
                                                    {isMissing ? 'EXIF assente' : 'Fuori tolleranza'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 font-mono text-[12px] text-slate-600">
                                                {formatDateTime(r.exifTimestamp)}
                                                {r.expectedTimestamp && (
                                                    <div className="text-[11px] text-slate-400">
                                                        atteso {formatDateTime(r.expectedTimestamp)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <span className="font-bold text-amber-700">{formatDiff(r.diffSeconds)}</span>
                                                {r.tolleranzaMinSnapshot != null && (
                                                    <div className="text-[11px] text-slate-400 font-normal">
                                                        tolleranza {r.tolleranzaMinSnapshot} min
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Paginazione */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                            Pagina <span className="font-bold text-slate-700">{(data?.number ?? 0) + 1}</span> di {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0 || loading}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page >= totalPages - 1 || loading}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllertMediaModal;
